import memoize from 'memoizee';
import config from '@/constants/config';
import coins from '@/constants/coins';
import { backOff } from 'exponential-backoff';
import Request from '@/utils/Request';
import { arrayToHashmap, flattenArray } from '@/utils/Array';
import { sequentialPromiseMap } from '@/utils/Async';
import getPricesForAssets from '@/utils/data/getPricesForAssets';
import getFactoryV2Pools from '@/utils/data/getFactoryV2Pools';
import getFactoryCryptoPools from '@/utils/data/getFactoryCryptoPools';

const MAX_ADDRESSES_PER_COINGECKO_REQUEST = 30;
const NATIVE_ETH_ADDRESS = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';
const KP3R_ADDRESS_ON_ETHEREUM = '0x1ceb5cb57c4d4e2b2433641b95dd330a33185a44';
const RKP3R_ADDRESS_ON_ETHEREUM = '0xedb67ee1b171c4ec66e6c10ec43edbba20fae8e9';

const highestPoolUsdTotal = ({ poolUsdTotal: poolUsdTotalA }, { poolUsdTotal: poolUsdTotalB }) => (
  poolUsdTotalA > poolUsdTotalB ? -1 :
    poolUsdTotalA < poolUsdTotalB ? 1 : 0
);

const getPricesForTokens = memoize(async (addresses, platform = config.coingeckoPlatformId) => {
  const attachRkp3rPrice = platform === 'ethereum';

  // eslint-disable-next-line no-param-reassign
  if (attachRkp3rPrice) addresses = addresses.concat(KP3R_ADDRESS_ON_ETHEREUM);

  // https://defillama.com/docs/api
  const pricesChunks = await sequentialPromiseMap(addresses, (addressesChunk) => (
    backOff(() => Request.get(`https://coins.llama.fi/prices/current/${addressesChunk.map((a) => `${platform}:${a}`).join(',')}`), {
      retry: (e, attemptNumber) => {
        console.log(`defillama retrying!`, { attemptNumber, addressesChunk });
        return true;
      },
    })
      .then((response) => response.json())
      .then(({ coins: prices }) => arrayToHashmap(Array.from(Object.entries(prices)).map(([platformAndAddress, { price: usdPrice }]) => [
        platformAndAddress.split(':')[1].toLowerCase(),
        usdPrice,
      ])))
  ), MAX_ADDRESSES_PER_COINGECKO_REQUEST);

  let mergedPrices = Object.assign({}, ...pricesChunks);

  const attachNativeEthPrice = addresses.some((address) => address?.toLowerCase() === NATIVE_ETH_ADDRESS);
  if (attachNativeEthPrice) {
    const coingeckoId = platform;
    mergedPrices[NATIVE_ETH_ADDRESS] = (await getPricesForAssets([coingeckoId]))[coingeckoId];
  }

  if (attachRkp3rPrice) {
    // Estimation: rkp3r is a kp3r option redeemable for 50% asset price
    mergedPrices[RKP3R_ADDRESS_ON_ETHEREUM] = mergedPrices[KP3R_ADDRESS_ON_ETHEREUM] * 0.1;
  }

  // If some prices couldn't be retrieved for this platform+address on coingecko,
  // fall back to retrieving price from coingecko using coingecko id from coin config.
  const addressesWithMissingPrices = addresses.filter((address) => !mergedPrices[address?.toLowerCase()]);
  if (addressesWithMissingPrices.length > 0) {
    const coinsArray = Array.from(Object.values(coins));
    const correspondingCoins = addressesWithMissingPrices.map((address) => (
      coinsArray.find((coin) => address.toLowerCase() === coin.address?.toLowerCase())
    ));

    const coingeckoIds = correspondingCoins.map((coin) => coin?.getCoingeckoId);
    const pricesForCoingeckoIds = await getPricesForAssets(coingeckoIds);

    const missingPricesMap = arrayToHashmap(addressesWithMissingPrices.map((address, i) => [
      address.toLowerCase(),
      pricesForCoingeckoIds[i],
    ]));

    mergedPrices = {
      ...mergedPrices,
      ...missingPricesMap,
    };
  }

  // And if the previous fallback wasn't enough, use facto pool data to try to
  // infer a price for missing assets
  // Note: this logic can be extended to main pools if needed, we're starting simple
  const addressesWithMissingPrices2 = addresses.filter((address) => !mergedPrices[address?.toLowerCase()]);
  if (addressesWithMissingPrices2.length > 0) {
    const [factoV2Pools, factoCryptoPools] = await Promise.all([
      getFactoryV2Pools(),
      getFactoryCryptoPools(),
    ]);

    const allFactoCoins = flattenArray([...factoV2Pools, ...factoCryptoPools].map(({ coins, usdTotal }) => coins.map((coin) => ({ ...coin, poolUsdTotal: usdTotal }))));
    const missingPricesMap = arrayToHashmap(addressesWithMissingPrices2.map((tokenAddress) => {
      const missingPriceCoinInfo = allFactoCoins.filter(({ address }) => (
        address.toLowerCase() === tokenAddress.toLowerCase()
      )).sort(highestPoolUsdTotal)[0];

      return [
        tokenAddress.toLowerCase(),
        missingPriceCoinInfo?.usdPrice,
      ];
    }));

    mergedPrices = {
      ...mergedPrices,
      ...missingPricesMap,
    };
  }

  return mergedPrices;
}, {
  promise: true,
  maxAge: 2 * 60 * 1000, // 2 min
  primitive: true,
});

const getPricesArrayForTokens = async (addresses, platform) => {
  const tokenPrices = await getPricesForTokens(addresses, platform);
  return addresses.map((address) => tokenPrices[address.toLowerCase()]);
};

export default getPricesForTokens;
export { getPricesArrayForTokens };
